/**
 * Client-side usage telemetry (App Usage, migration 137).
 *
 * Batches page_view / session_start / feature events and posts them to
 * POST /api/v1/usage/events (same-origin, no CSP change needed). The server
 * hook in the permission helper covers api_calls; this module covers what the
 * server cannot see: which page is on screen and UI-only interactions.
 *
 * Suppression:
 *  - dev builds are a full no-op (also neutralises React StrictMode's
 *    double-invoked effects, which are dev-only),
 *  - demo mode (Ctrl+Shift+D, localStorage 'demo-mode-hide-nav') is skipped
 *    per call so live demos don't pollute the data.
 *
 * Delivery: in-memory queue (cap 200, drop-oldest), flushed every 10s and on
 * visibilitychange/pagehide via fetch keepalive (max 25 events per batch —
 * keepalive bodies are limited to 64KB). A failed batch is retried once on the
 * next flush, then dropped: telemetry must never grow unbounded or retry-storm.
 *
 * Privacy: no content, no durations — event type, canonical page path,
 * feature key and timestamps only (see context/docs/app-usage.md).
 */
import { matchNavPath } from '../hooks/usePageMeta';

interface QueuedEvent {
    type: 'page_view' | 'session_start' | 'feature';
    page?: string;
    feature?: string;
    ts: number;
    meta?: Record<string, unknown>;
    retried?: boolean;
}

const DISABLED = import.meta.env.DEV;
const SESSION_KEY = 'reporting_app_session_id';
const FLUSH_INTERVAL_MS = 10_000;
const MAX_BATCH = 25;
const MAX_QUEUE = 200;
const PAGE_VIEW_DEDUPE_MS = 500;

let queue: QueuedEvent[] = [];
let sessionId: string | null = null;
let initialized = false;
let flushInFlight = false;
let lastPageViewPath = '';
let lastPageViewAt = 0;

function demoModeActive(): boolean {
    try {
        return localStorage.getItem('demo-mode-hide-nav') === 'true';
    } catch {
        return false;
    }
}

function ensureInit(): void {
    if (initialized || DISABLED) return;
    initialized = true;

    try {
        sessionId = sessionStorage.getItem(SESSION_KEY);
        if (!sessionId) {
            sessionId = crypto.randomUUID();
            sessionStorage.setItem(SESSION_KEY, sessionId);
            enqueue({ type: 'session_start', ts: Date.now() });
        }
    } catch {
        sessionId = null;
    }

    window.setInterval(() => { void flush(); }, FLUSH_INTERVAL_MS);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') void flush();
    });
    window.addEventListener('pagehide', () => { void flush(); });
}

function enqueue(event: QueuedEvent): void {
    if (queue.length >= MAX_QUEUE) queue.shift();
    queue.push(event);
}

async function flush(): Promise<void> {
    if (flushInFlight || queue.length === 0) return;
    flushInFlight = true;
    const batch = queue.slice(0, MAX_BATCH);
    queue = queue.slice(batch.length);
    try {
        const res = await fetch('/api/v1/usage/events', {
            method: 'POST',
            keepalive: true,
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                sessionId,
                events: batch.map(({ retried: _r, ...e }) => e),
            }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch {
        // One retry on the next flush, then drop — never spam, never grow.
        const retryable = batch.filter(e => !e.retried).map(e => ({ ...e, retried: true }));
        queue = [...retryable, ...queue].slice(0, MAX_QUEUE);
    } finally {
        flushInFlight = false;
    }
}

/** Auto page-view tracking — called from Layout on every route change. */
export function trackPageView(pathname: string): void {
    if (DISABLED || demoModeActive()) return;
    ensureInit();
    const page = matchNavPath(pathname);
    const now = Date.now();
    if (page === lastPageViewPath && now - lastPageViewAt < PAGE_VIEW_DEDUPE_MS) return;
    lastPageViewPath = page;
    lastPageViewAt = now;
    enqueue({ type: 'page_view', page, ts: now });
}

/**
 * Explicit feature event for UI-only interactions the server cannot see
 * (tab switches, drilldown opens, clipboard exports …).
 * Convention: '<page-key>.<action>', e.g. 'customer-360.tab-market'.
 * `meta` carries counts/ids only — never content.
 */
export function trackFeature(feature: string, meta?: Record<string, unknown>): void {
    if (DISABLED || demoModeActive() || !feature) return;
    ensureInit();
    enqueue({
        type: 'feature',
        feature: feature.slice(0, 100),
        page: matchNavPath(window.location.pathname),
        ts: Date.now(),
        meta,
    });
}
